import { Button, Dialog, DialogPanel, TextInput } from "@tremor/react";
import Badge from "../components/Badge.jsx";
import DeleteSiteDialog from "../components/DeleteSiteDialog.jsx";
import {
  ChevronRight,
  Globe,
  Layers,
  Plus,
  Trash2,
  X,
  Activity,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  useSites,
  useQuickSetup
} from "../queries/sites.js";

const STATUS_BADGE_COLOR = { up: "emerald", degraded: "amber", down: "red", paused: "gray" };

const STATUS_DOT = {
  up: "bg-green-500",
  degraded: "bg-yellow-400",
  down: "bg-red-500",
  paused: "bg-gray-400",
};

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function QuickSetupWizard({ onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const quickSetupMutation = useQuickSetup();
  const saving = quickSetupMutation.isPending;

  async function handleFinish(e) {
    e.preventDefault();
    if (!name || !url) return;
    try {
      const site = await quickSetupMutation.mutateAsync({
        name,
        url,
      });
      toast.success(`Site "${site.name}" created with ${site.monitor_count} monitors!`);
      navigate(`/sites/${site.id}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Failed to create site");
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <form onSubmit={handleFinish} className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Add New Site</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Configure a composite site dashboard with plain language diagnostics.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Site Name
                </label>
                <TextInput
                  autoFocus
                  required
                  value={name}
                  onValueChange={setName}
                  placeholder="e.g. My Online Store"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-400">
                  A friendly display name for your dashboard.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Website URL
                </label>
                <TextInput
                  required
                  value={url}
                  onValueChange={setUrl}
                  placeholder="https://example.com"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-400">
                  We will scan this site to auto-configure optimal checks (Shopify checkout, page speeds, SSL, domain registration, and visual changes).
                </p>
              </div>
            </div>

            {saving && (
              <div className="flex flex-col items-center justify-center p-6 space-y-3 rounded-lg border border-teal-100 bg-teal-50/50">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                <div className="text-center">
                  <p className="text-sm font-semibold text-teal-900">Scanning & provisioning...</p>
                  <p className="text-xs text-teal-600/80 mt-1">Analyzing platform layout & generating optimal checkers. This takes a few seconds.</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Provisioning..." : "Scan & Add Site"}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function SiteCard({ site, onDelete }) {
  const [showDelete, setShowDelete] = useState(false);

  function openDelete(e) {
    e.preventDefault();
    e.stopPropagation();
    setShowDelete(true);
  }

  return (
    <>
      <Link
        to={`/sites/${site.id}`}
        className="group relative flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-teal-300 hover:shadow-md"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`mt-0.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${STATUS_DOT[site.status] ?? STATUS_DOT.up}`}
            />
            <h3 className="text-sm font-semibold text-gray-900">{site.name}</h3>
          </div>
          <button
            type="button"
            onClick={openDelete}
            className="rounded p-1 text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-1 text-xs text-gray-400 font-mono truncate">{site.url}</p>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-4">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Layers className="h-3.5 w-3.5" />
              <span>{site.monitor_count} Components</span>
            </div>
            {site.avg_response_time_ms != null && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Activity className="h-3.5 w-3.5" />
                <span>{fmtMs(site.avg_response_time_ms)}</span>
              </div>
            )}
          </div>
          <Badge color={STATUS_BADGE_COLOR[site.status] ?? "gray"}>
            {site.status.toUpperCase()}
          </Badge>
        </div>

        <ChevronRight className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300 transition group-hover:text-teal-400" />
      </Link>
      {showDelete && (
        <DeleteSiteDialog
          site={site}
          onClose={() => setShowDelete(false)}
          onDeleted={onDelete}
        />
      )}
    </>
  );
}

export default function Sites() {
  const [showWizard, setShowWizard] = useState(false);
  const qc = useSites();
  const sites = qc.data ?? [];
  const loading = qc.isPending;

  function handleDeleted() {
    qc.refetch();
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor all your client properties as a single composite site view.
          </p>
        </div>
        <p className="text-sm text-gray-400">Loading your sites...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor all your client properties as a single composite site view.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setShowWizard(true)}>
          Add New Site
        </Button>
      </div>

      {/* Empty state */}
      {sites.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-20 text-center">
          <Globe className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-4 text-base font-semibold text-gray-700">No Sites Monitored</p>
          <p className="mt-1 max-w-sm text-sm text-gray-400">
            Create a composite site view for your website to run platform checks and surface only the useful diagnostics.
          </p>
          <Button className="mt-6" icon={Plus} onClick={() => setShowWizard(true)}>
            Add New Site
          </Button>
        </div>
      )}

      {/* Sites Grid */}
      {sites.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((s) => (
            <SiteCard key={s.id} site={s} onDelete={handleDeleted} />
          ))}
        </div>
      )}

      {/* Dialogs */}
      {showWizard && (
        <QuickSetupWizard
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
